import { searchService } from './search-service.js';

/**
 * The access level the current logged-in user has, as returned by
 * `users/getAccessLevel`. Only `isAdmin` is used to gate admin features today,
 * but the full payload is preserved for callers that need more detail.
 */
export interface UserAccessLevel {
    isAdmin: boolean;
    [key: string]: unknown;
}

/**
 * A minimal wrapper over the EUM user endpoints used by admin management
 * features (edit user, remove from group). It mirrors the base-URL and
 * request-body conventions of the legacy directory:
 *   - When `apiBaseUrl` is provided, calls go directly to `{base}/eum_api/v1/…`
 *     and bodies are single-stringified.
 *   - Otherwise calls are proxied through `/api/EUMAPI/{Verb}?apiUrl=…` and
 *     write bodies are DOUBLE-stringified so the proxy can forward them intact.
 */
export class UserService {
    constructor(private readonly apiBaseUrl = '') {}

    private get directBase(): string {
        return this.apiBaseUrl ? `${this.apiBaseUrl.replace(/\/$/, '')}/eum_api/v1/` : '';
    }

    private get usesProxy(): boolean {
        return !this.apiBaseUrl;
    }

    private headers(): Record<string, string> {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json; charset=utf-8',
        };
        const token = (window as any).EUMPortal?.bearerToken;
        if (token) headers['Authorization'] = token;
        return headers;
    }

    /** Build a request URL for the given relative API path and proxy verb. */
    private buildUrl(verb: 'Get' | 'Post' | 'Patch' | 'Delete', apiPath: string): string {
        return this.usesProxy
            ? `/api/EUMAPI/${verb}?apiUrl=${apiPath}`
            : `${this.directBase}${apiPath}`;
    }

    /**
     * Encode a write body. Through the proxy the payload must be double
     * stringified so `EUMAPIController` can forward the JSON string downstream.
     */
    private encodeBody(payload: unknown): string {
        return this.usesProxy
            ? JSON.stringify(JSON.stringify(payload))
            : JSON.stringify(payload);
    }

    /** Fetch the current user's access level (used to detect admins). */
    async getAccessLevel(): Promise<UserAccessLevel> {
        const response = await fetch(this.buildUrl('Get', 'users/getAccessLevel'), {
            method: 'GET',
            headers: this.headers(),
        });
        if (!response.ok) throw new Error(`Error fetching access level: ${response.status}`);
        return response.json();
    }

    /** Fetch the full user record from EUM Admin (not the search index). */
    async getUser<T = Record<string, unknown>>(userId: string): Promise<T> {
        const response = await fetch(this.buildUrl('Get', `users/${encodeURIComponent(userId)}`), {
            method: 'GET',
            headers: this.headers(),
        });
        if (!response.ok) throw new Error(`Error fetching user: ${response.status}`);
        return response.json();
    }

    /** PATCH changed fields (and optionally a base64 `photo`) for a user. */
    async updateUser(userId: string, payload: Record<string, unknown>): Promise<Response> {
        const response = await fetch(this.buildUrl('Patch', `users/${encodeURIComponent(userId)}`), {
            method: 'PATCH',
            headers: this.headers(),
            body: this.encodeBody(payload),
        });
        if (!response.ok) throw new Error(`Error updating user: ${response.status}`);
        return response;
    }

    /** Remove a user from a group. */
    async removeFromGroup(userId: string, groupId: string): Promise<Response> {
        const response = await fetch(
            this.buildUrl('Delete', `users/${encodeURIComponent(userId)}/memberOf/${encodeURIComponent(groupId)}`),
            {
                method: 'DELETE',
                headers: this.headers(),
            }
        );
        if (!response.ok) throw new Error(`Error removing user from group: ${response.status}`);
        return response;
    }

    /** Add an existing user to a group. */
    async addToGroup(userId: string, groupId: string): Promise<Response> {
        const response = await fetch(
            this.buildUrl('Post', `users/${encodeURIComponent(userId)}/memberOf/${encodeURIComponent(groupId)}`),
            {
                method: 'POST',
                headers: this.headers(),
            }
        );
        if (!response.ok) throw new Error(`Error adding user to group: ${response.status}`);
        return response;
    }

    /**
     * Invite (create) a new user. The `payload` carries the profile fields plus
     * an optional `InitialGroups` JSON array string to seed group membership.
     */
    async inviteUser<T = Record<string, unknown>>(payload: Record<string, unknown>): Promise<T> {
        const response = await fetch(this.buildUrl('Post', 'users'), {
            method: 'POST',
            headers: this.headers(),
            body: this.encodeBody(payload),
        });
        if (!response.ok) throw new Error(`Error inviting user: ${response.status}`);
        return response.json();
    }
}

// Re-export searchService presence is intentional: consumers that already have a
// bearer token set on searchService share the same window.EUMPortal token here.
export { searchService };
